import { Injectable } from '@angular/core';
import {
  AnalyticsBaseService,
  AnalyticsEventType,
  AnalyticsRoleUtils,
} from '@irembo-andela/irembogov3-common';
import { AnalyticsEvent } from '@irembo-andela/irembogov3-common';
import { KeycloakService } from 'keycloak-angular';
import { LocaleService } from '../locale.service';
import { environment } from 'src/environments/environment';
import mixpanel, { Config } from 'mixpanel-browser';

@Injectable({
  providedIn: 'root',
})
export class MixpanelService implements AnalyticsBaseService {
  private readonly APP_SESSION_ID_KEY = 'user_session_id';
  private readonly KC_SESSION_KEY = 'current_keycloak_sid';

  constructor(
    private keycloakService: KeycloakService,
    private localeService: LocaleService
  ) {
    if (environment.IS_SENDING_ANALYTICS_EVENTS_ACTIVE) {
      this.initialize({
        token: environment.MIXPANEL_TOKEN,
        options: {
          ignore_dnt: true, // By default, Mixpanel will not track users with Do Not Track enabled. This option will override that behavior.
        },
      });
    }
  }

  initialize(config: { token: string; options?: Partial<Config> }) {
    try {
      mixpanel.init(config.token, config.options);
    } catch (error) {
      console.error('Error initializing Mixpanel:', error);
    }
  }

  EXCLUDED_EVENTS: AnalyticsEventType[] = [
    // ... Add more events to exclude here
  ];

  async trackEvent(
    eventData: Pick<AnalyticsEvent, 'eventType'> & Partial<AnalyticsEvent>
  ) {
    try {
      if (!environment.IS_SENDING_ANALYTICS_EVENTS_ACTIVE) return;

      // Check if the event type is in the excluded events list
      if (
        this.EXCLUDED_EVENTS.includes(eventData.eventType as AnalyticsEventType)
      ) {
        return;
      }

      const isAuthenticated = await this.keycloakService.isLoggedIn();
      const user = isAuthenticated
        ? await this.keycloakService.loadUserProfile()
        : null;
      const roles = isAuthenticated
        ? await this.keycloakService.getUserRoles()
        : [];

      const tokenParsed =
        this.keycloakService.getKeycloakInstance().idTokenParsed;
      const keycloakSid = tokenParsed?.['sid'];

      if (keycloakSid) {
        this.initSession(keycloakSid);
      }

      // Create standard properties
      const standardProperties = {
        user_id: user?.id,
        user_type: AnalyticsRoleUtils.getUserType(roles),
        user_role: AnalyticsRoleUtils.getUserRole(roles),
        timestamp: new Date().toISOString(),
        distinct_id: user?.id,
      };

      // Extract metadata from eventData
      const { eventType, metadata, ...otherProps } = eventData;

      // Track the event with flattened properties
      mixpanel.track(eventType, {
        ...standardProperties,
        ...otherProps,
        ...metadata, // Spread metadata at root level
      });

      // Update user profile if authenticated
      if (isAuthenticated && user) {
        mixpanel.people.set({
          $email: user.email,
          $name: `${user.firstName} ${user.lastName}`,
          user_type: standardProperties.user_type,
          user_role: standardProperties.user_role,
        });
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  setUser(userId: string, userProperties?: Record<string, any>) {
    mixpanel.identify(userId);
    if (userProperties) {
      mixpanel.people.set(userProperties);
    }
  }

  /**
   * Initializes the analytics session.
   * Compares the current Keycloak SID with the stored one to
   * determine if a new app_session_id needs to be generated.
   *
   * @param keycloakSid The 'sid' claim from the Keycloak ID token.
   */
  initSession(keycloakSid: string): void {
    const storedKcSid = localStorage.getItem(this.KC_SESSION_KEY);
    let appSessionId = localStorage.getItem(this.APP_SESSION_ID_KEY);

    // Check if the Keycloak session is new (or if our app ID is missing)
    if (keycloakSid !== storedKcSid || !appSessionId) {
      // This is a NEW session (login or timeout)
      // 1. Generate a new safe ID
      appSessionId = crypto.randomUUID();

      // 2. Store the new safe ID
      localStorage.setItem(this.APP_SESSION_ID_KEY, appSessionId);

      // 3. Store the current Keycloak SID to check against next time
      localStorage.setItem(this.KC_SESSION_KEY, keycloakSid);
    }

    // 4. Register the appSessionId (either new or existing)
    mixpanel.register({
      user_session_id: appSessionId,
    });
  }

  reset() {
    const mp = (window as any).mixpanel;
    if (mp && typeof mp.reset === 'function') {
      mp.reset();
    }
    localStorage.removeItem(this.APP_SESSION_ID_KEY);
    localStorage.removeItem(this.KC_SESSION_KEY);
  }
}
